import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { ThemeProvider } from "@/lib/theme-provider"
import { SidebarProvider } from "@/lib/sidebar-context"
import { Toaster } from "@/components/ui/sonner"
import { MainLayout } from "@/components/layout/MainLayout"
import { SignIn } from "@/features/auth/pages/SignIn"
import { Unauthorized } from "@/features/auth/pages/Unauthorized"
import { Dashboard } from "@/features/dashboard/pages/Dashboard"
import { AnnouncementsList } from "@/features/announcements/pages/AnnouncementsList"
import { AnnouncementEditor } from "@/features/announcements/pages/AnnouncementEditor"
import { RemindersList } from "@/features/reminders/pages/RemindersList"
import { ReminderEditor } from "@/features/reminders/pages/ReminderEditor"
import { ReminderReportPage } from "@/features/reminders/pages/ReminderReport"
import { BlogList } from "@/features/blogs/pages/BlogList"
import { BlogEditor } from "@/features/blogs/pages/BlogEditor"
import { VoiceNotes } from "@/features/voice-notes/pages/VoiceNotes"
import { VoiceNoteDetail } from "@/features/voice-notes/pages/VoiceNoteDetail"
import { MemberLevels } from "@/features/member-levels/pages/MemberLevels"
import { MemberLevelCodes } from "@/features/member-levels/pages/MemberLevelCodes"
import { Users } from "@/features/users/pages/Users"
import { Settings } from "@/features/settings/pages/Settings"
import { authService } from "@/features/auth/services/auth.service"
import "./App.css"

function App() {
  const hasTokenOnLoad = authService.isAuthenticated()
  const [authChecking, setAuthChecking] = useState(hasTokenOnLoad)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    if (!authChecking) return
    authService.validateSession().then((valid) => {
      setIsAuthenticated(valid)
      setAuthChecking(false)
    })
  }, [authChecking])

  useEffect(() => {
    const onAuthChange = () => {
      if (!authService.isAuthenticated()) {
        setIsAuthenticated(false)
        setAuthChecking(false)
      }
    }
    window.addEventListener("auth-logout", onAuthChange)
    window.addEventListener("storage", onAuthChange)
    return () => {
      window.removeEventListener("auth-logout", onAuthChange)
      window.removeEventListener("storage", onAuthChange)
    }
  }, [])

  const isAdmin = authService.isAdmin()

  if (authChecking) {
    return (
      <ThemeProvider defaultTheme="system" storageKey="voice-dictation-theme">
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="text-muted-foreground">Checking session...</div>
        </div>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider defaultTheme="system" storageKey="voice-dictation-theme">
      <SidebarProvider>
        <Toaster />
        <BrowserRouter>
      <Routes>
        <Route
          path="/signin"
          element={
            isAuthenticated
              ? isAdmin
                ? <Navigate to="/dashboard" replace />
                : <Navigate to="/unauthorized" replace />
              : <SignIn />
          }
        />
        <Route
          path="/unauthorized"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : <Unauthorized />
          }
        />
        <Route
          path="/"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <Dashboard />
              </MainLayout>
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <Dashboard />
              </MainLayout>
            )
          }
        />
        <Route
          path="/announcements"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <AnnouncementsList />
              </MainLayout>
            )
          }
        />
        <Route
          path="/announcements/new"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <AnnouncementEditor />
              </MainLayout>
            )
          }
        />
        <Route
          path="/announcements/:id/edit"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <AnnouncementEditor />
              </MainLayout>
            )
          }
        />
        <Route
          path="/reminders"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <RemindersList />
              </MainLayout>
            )
          }
        />
        <Route
          path="/reminders/new"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <ReminderEditor />
              </MainLayout>
            )
          }
        />
        <Route
          path="/reminders/:id/edit"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <ReminderEditor />
              </MainLayout>
            )
          }
        />
        <Route
          path="/reminders/:id/report"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <ReminderReportPage />
              </MainLayout>
            )
          }
        />
        <Route
          path="/blogs"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <BlogList />
              </MainLayout>
            )
          }
        />
        <Route
          path="/blogs/new"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <BlogEditor />
              </MainLayout>
            )
          }
        />
        <Route
          path="/blogs/edit/:id"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <BlogEditor />
              </MainLayout>
            )
          }
        />
        <Route
          path="/voice-notes"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <VoiceNotes />
              </MainLayout>
            )
          }
        />
        <Route
          path="/voice-notes/:id"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <VoiceNoteDetail />
              </MainLayout>
            )
          }
        />
        <Route
          path="/member-levels"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <MemberLevels />
              </MainLayout>
            )
          }
        />
        <Route
          path="/member-level-codes"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <MemberLevelCodes />
              </MainLayout>
            )
          }
        />
        <Route
          path="/users"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <Users />
              </MainLayout>
            )
          }
        />
        <Route
          path="/settings"
          element={
            !isAuthenticated
              ? <Navigate to="/signin" replace />
              : !isAdmin
                ? <Unauthorized />
                : (
              <MainLayout>
                <Settings />
              </MainLayout>
            )
          }
        />
      </Routes>
        </BrowserRouter>
      </SidebarProvider>
    </ThemeProvider>
  )
}

export default App
